import { supabase } from '../lib/supabase'

export interface ActivityLog {
  id: string
  activity_type: string
  entity_type: string
  entity_id: string
  description: string
  metadata?: any
  created_by?: string
  created_by_name?: string
  created_at: string
}

async function insertActivityRow(row: Record<string, unknown>) {
  let { data, error } = await supabase.from('activity_logs').insert([row])

  if (error && /user_id/i.test(error.message)) {
    const { user_id: _userId, ...withoutUserId } = row
    ;({ data, error } = await supabase.from('activity_logs').insert([withoutUserId]))
  }

  return { data, error }
}

function resolveCreatorName(row: {
  creator?: { first_name?: string; last_name?: string; email?: string } | null
  metadata?: { created_by_name?: string } | null
}): string | undefined {
  const creator = row.creator
  if (creator) {
    const name = `${creator.first_name || ''} ${creator.last_name || ''}`.trim()
    return name || creator.email || undefined
  }
  if (row.metadata?.created_by_name) {
    return row.metadata.created_by_name
  }
  return undefined
}

function mapActivityRow(row: any): ActivityLog {
  const { creator, ...rest } = row
  return {
    ...rest,
    created_by_name: resolveCreatorName(row),
  }
}

export function formatActivityMeta(activity: ActivityLog): string {
  const timestamp = new Date(activity.created_at).toLocaleString()
  if (activity.created_by_name) {
    return `${timestamp} · By ${activity.created_by_name}`
  }
  return timestamp
}

/**
 * Log an activity for an entity
 */
export async function logActivity(data: {
  activity_type: string
  entity_type: 'lead' | 'customer' | 'property' | 'job' | 'contract' | 'proposal'
  entity_id: string
  description: string
  metadata?: any
}) {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    const { data: activity, error } = await insertActivityRow({
      activity_type: data.activity_type,
      entity_type: data.entity_type,
      entity_id: data.entity_id,
      description: data.description,
      metadata: data.metadata,
      created_by: user?.id || null,
      user_id: user?.id || null,
    })

    if (error) {
      console.error('Error logging activity:', error)
      return null
    }

    return activity
  } catch (error) {
    console.error('Error logging activity:', error)
    return null
  }
}

/**
 * Fetch activities for an entity
 */
export async function getEntityActivities(entityType: string, entityId: string): Promise<ActivityLog[]> {
  try {
    const { data, error } = await supabase
      .from('activity_logs')
      .select('*')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching activities:', error)
      return []
    }

    const rows = data || []
    if (rows.length === 0) return []

    const creatorIds = Array.from(
      new Set(rows.map((row) => row.created_by).filter((id): id is string => Boolean(id)))
    )

    let profilesById: Record<
      string,
      { first_name?: string; last_name?: string; email?: string }
    > = {}

    if (creatorIds.length > 0) {
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email')
        .in('id', creatorIds)

      if (!profilesError && profiles) {
        profilesById = Object.fromEntries(profiles.map((profile) => [profile.id, profile]))
      }
    }

    return rows.map((row) =>
      mapActivityRow({
        ...row,
        creator: row.created_by ? profilesById[row.created_by] ?? null : null,
      })
    )
  } catch (error) {
    console.error('Error fetching activities:', error)
    return []
  }
}

/**
 * Common activity types
 */
export const ActivityTypes = {
  // Property activities
  PROPERTY_CREATED: 'property_created',
  PROPERTY_UPDATED: 'property_updated',
  PROPERTY_DELETED: 'property_deleted',

  // Job activities
  JOB_CREATED: 'job_created',
  JOB_AUTO_CREATED: 'job_auto_created',
  JOB_UPDATED: 'job_updated',
  JOB_COMPLETED: 'job_completed',

  // Customer activities
  CUSTOMER_CREATED: 'customer_created',
  CUSTOMER_UPDATED: 'customer_updated',

  // Lead activities
  LEAD_CREATED: 'lead_created',
  LEAD_UPDATED: 'lead_updated',
  LEAD_CONVERTED: 'lead_converted',

  // Contract activities
  CONTRACT_CREATED: 'contract_created',
  CONTRACT_UPDATED: 'contract_updated',
  CONTRACT_SENT_FOR_SIGNATURE: 'contract_sent_for_signature',
  CONTRACT_SIGNED: 'contract_signed',

  // Proposal activities
  PROPOSAL_CREATED: 'proposal_created',
  PROPOSAL_UPDATED: 'proposal_updated',
  PROPOSAL_SENT: 'proposal_sent',
  PROPOSAL_ACCEPTED: 'proposal_accepted',
  PROPOSAL_REJECTED: 'proposal_rejected',

  // Payment activities
  PAYMENT_RECEIVED: 'payment_received',
  PAYMENT_PROCESSED: 'payment_processed',
  PAYMENT_VOIDED: 'payment_voided',

  // Proposal request activities
  PROPOSAL_REQUEST_CREATED: 'proposal_request_created',
  PROPOSAL_REQUEST_UPDATED: 'proposal_request_updated',

  // Invoice activities
  INVOICE_CREATED: 'invoice_created',
  INVOICE_UPDATED: 'invoice_updated',
  STATEMENT_EMAILED: 'statement_emailed',
} as const

export interface LinkedParentIds {
  lead_id?: string | null
  customer_id?: string | null
  property_id?: string | null
}

function sanitizeEntityId(id?: string | null): string | null {
  if (!id || id === '' || id === 'undefined' || id === 'null') return null
  return id
}

/**
 * Log the same activity on each linked parent entity (lead, customer, property).
 */
export async function logActivityOnLinkedParents(params: {
  parents: LinkedParentIds
  activity_type: string
  description: string
  metadata?: Record<string, unknown>
}) {
  const leadId = sanitizeEntityId(params.parents.lead_id)
  const customerId = sanitizeEntityId(params.parents.customer_id)
  const propertyId = sanitizeEntityId(params.parents.property_id)

  const tasks: Promise<unknown>[] = []

  if (leadId) {
    tasks.push(
      logActivity({
        activity_type: params.activity_type,
        entity_type: 'lead',
        entity_id: leadId,
        description: params.description,
        metadata: params.metadata,
      })
    )
  }
  if (customerId) {
    tasks.push(
      logActivity({
        activity_type: params.activity_type,
        entity_type: 'customer',
        entity_id: customerId,
        description: params.description,
        metadata: params.metadata,
      })
    )
  }
  if (propertyId) {
    tasks.push(
      logActivity({
        activity_type: params.activity_type,
        entity_type: 'property',
        entity_id: propertyId,
        description: params.description,
        metadata: params.metadata,
      })
    )
  }

  await Promise.all(tasks)
}

